import memoize from 'memoize-one';
import { indexBy, pathEq, pathOr, propOr } from '../utils/fp';
import { withIndex } from '../utils';
import { getDataflow, getDisplay, getTimeDimensionOrders } from './router';
import { getData, getDataflowName } from './sdmx';
import { customAttributes } from '../lib/settings';
import * as rules from '../lib/rules';
import { DISPLAY_BOTH, DISPLAY_CODE, DISPLAY_LABEL } from '../utils/constants';
import { getDataflowCustomAttributes } from '../lib/sdmx/computors/observations';
import { createSelector } from './createSelector';

const getVisState = state => state?.vis;

export const getVisActionId = memoize(state => getVisState(state)?.actionId);
export const getIsFull = memoize(state => getVisState(state)?.isFull);
export const getIsOpeningFullscreen = memoize(state => getVisState(state)?.isOpeningFullscreen);
export const getFilter = memoize(state => getVisState(state)?.filter);
export const getHasCsvDlStart = memoize(state => getVisState(state)?.hasCsvDlStart);
export const getHasShared = memoize(state => getVisState(state)?.shared);
export const getFiltersSelectionKey = memoize(state => getVisState(state)?.filtersSelectionKey);
export const getHasDownloadedExcel = memoize(state =>
  pathEq('excel', ['excel', 'id'], getVisState(state)),
);

export const getHasDownloadedPng = memoize(state =>
  pathEq('png', ['png', 'id'], getVisState(state)),
);

export const getVisDimensionFormat = accessors =>
  memoize(state => {
    const display = getDisplay(state);
    const configs = {
      [DISPLAY_LABEL]: rules.dimensionValueDisplay(DISPLAY_LABEL, accessors),
      [DISPLAY_CODE]: rules.dimensionValueDisplay(DISPLAY_CODE, accessors),
      [DISPLAY_BOTH]: rules.dimensionValueDisplay(DISPLAY_BOTH, accessors),
    };
    return configs[display];
  });

export const getDataDimensions = memoize(state => {
  const data = getData(state);
  const dimensions = pathOr([], ['structure', 'dimensions', 'observation'], data);

  return dimensions.map((dimension, index) => ({
    ...dimension,
    index,
    values: withIndex(propOr([], 'values', dimension)),
  }));
});

export const getVisDataDimensions = memoize(state => {
  const dimensions = getDataDimensions(state);

  const many = dimensions.filter(dim => propOr([], 'values', dim).length > 1);
  const one = dimensions.filter(dim => propOr([], 'values', dim).length <= 1);

  return {
    many: indexBy(dim => dim.id, many),
    one: indexBy(dim => dim.id, one),
  };
});

// Keyed on the data rather than on `state`: the observation model in selectors/data.js is
// built on this object, so a new one on every dispatch would rebuild the model each time.
export const getCustomAttributes = createSelector(
  () => [getData],
  data => getDataflowCustomAttributes(data, customAttributes),
);

export const getIsTimeInverted = memoize(state => {
  const orders = getTimeDimensionOrders(state);
  return Object.values(orders)[0] === true;
});

export const getAvailableCharts = memoize(state => rules.getAvailableChartTypes(getData(state)));

export const getVisDataflow = memoize(state => {
  const dataflow = getDataflow(state);
  const name = getDataflowName(state);
  return {
    id: dataflow.dataflowId,
    name,
  };
});
